import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaShieldAlt, FaKey, FaCopy, FaDownload, FaEye, FaEyeSlash } from "react-icons/fa";

const API_URL = "https://awriter-backend-si7g.onrender.com";

const TwoFactorSettings = () => {
  const [twoFactorStatus, setTwoFactorStatus] = useState({
    twoFactorEnabled: false,
    hasBackupCodes: false,
    unusedBackupCodes: 0,
  });
  
  const [setupData, setSetupData] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [verificationToken, setVerificationToken] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const token = localStorage.getItem("adminToken");
  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchTwoFactorStatus();
  }, []);

  const fetchTwoFactorStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/2fa/status`, axiosConfig);
      setTwoFactorStatus(response.data);
    } catch (error) {
      console.error("Error fetching 2FA status:", error);
      setError("Failed to load 2FA status");
    }
  };

  const handleSetup2FA = async () => {
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/2fa/setup`, {}, axiosConfig);
      setSetupData(response.data);
      setShowSetup(true);
      setSuccess("Scan the QR code with your authenticator app");
    } catch (error) {
      console.error("Error setting up 2FA:", error);
      setError("Failed to setup 2FA. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!verificationToken || verificationToken.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await axios.post(
        `${API_URL}/api/admin/2fa/verify`,
        { token: verificationToken },
        axiosConfig
      );
      
      setSuccess("2FA has been enabled successfully!");
      setShowSetup(false);
      setSetupData(null);
      setVerificationToken("");
      fetchTwoFactorStatus();
    } catch (error) {
      console.error("Error verifying 2FA:", error);
      setError("Invalid verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePassword) {
      setError("Password is required to disable 2FA");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await axios.post(
        `${API_URL}/api/admin/2fa/disable`,
        { password: disablePassword },
        axiosConfig
      );
      
      setSuccess("2FA has been disabled successfully");
      setShowDisableForm(false);
      setDisablePassword("");
      fetchTwoFactorStatus();
    } catch (error) {
      console.error("Error disabling 2FA:", error);
      setError(error.response?.data?.error || "Failed to disable 2FA");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess("Copied to clipboard!");
    setTimeout(() => setSuccess(""), 2000);
  };

  const downloadBackupCodes = () => {
    if (!setupData?.backupCodes) return;
    
    const content = `AWriter Admin Backup Codes
Generated: ${new Date().toLocaleDateString()}

Instructions:
- Keep these codes secure
- Each code can only be used once
- Use them if you lose access to your authenticator app

Backup Codes:
${setupData.backupCodes.map((code, index) => `${index + 1}. ${code}`).join('\n')}`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'awriter-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    setSuccess("Backup codes downloaded!");
    setTimeout(() => setSuccess(""), 3000);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="flex items-center gap-3 mb-6">
        <FaShieldAlt className="text-2xl text-blue-600" />
        <h2 className="text-2xl font-bold">Two-Factor Authentication</h2>
      </div>

      {/* Status Display */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">
              2FA Status: {" "}
              <span className={twoFactorStatus.twoFactorEnabled ? "text-green-600" : "text-red-600"}>
                {twoFactorStatus.twoFactorEnabled ? "Enabled" : "Disabled"}
              </span>
            </h3>
            {twoFactorStatus.twoFactorEnabled && (
              <p className="text-sm text-gray-600 mt-1">
                Backup codes available: {twoFactorStatus.unusedBackupCodes}
              </p>
            )}
          </div>
          <div className={`w-4 h-4 rounded-full ${twoFactorStatus.twoFactorEnabled ? "bg-green-500" : "bg-red-500"}`}></div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-600 p-3 rounded-lg mb-4">
          {success}
        </div>
      )}

      {/* Enable 2FA Section */}
      {!twoFactorStatus.twoFactorEnabled && !showSetup && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="font-semibold text-lg mb-2">Secure Your Account</h3>
            <p className="text-gray-700 mb-4">
              Two-factor authentication adds an extra layer of security to your admin account. 
              You'll need your phone and authenticator app to sign in.
            </p>
            <button
              onClick={handleSetup2FA}
              disabled={loading}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <FaShieldAlt />
              {loading ? "Setting up..." : "Enable 2FA"}
            </button>
          </div>
        </div>
      )}

      {/* Setup Process */}
      {showSetup && setupData && (
        <div className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="font-semibold text-lg mb-4">Setup Two-Factor Authentication</h3>
            
            {/* Step 1: QR Code */}
            <div className="mb-6">
              <h4 className="font-medium mb-2">Step 1: Scan QR Code</h4>
              <p className="text-sm text-gray-600 mb-3">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
              </p>
              <div className="bg-white p-4 rounded-lg inline-block border">
                <img src={setupData.qrCodeUrl} alt="2FA QR Code" className="w-48 h-48" />
              </div>
              <div className="mt-2">
                <p className="text-xs text-gray-500">Can't scan? Enter this code manually:</p>
                <div className="flex items-center gap-2 mt-1">
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm break-all">
                    {setupData.secret}
                  </code>
                  <button
                    onClick={() => copyToClipboard(setupData.secret)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <FaCopy />
                  </button>
                </div>
              </div>
            </div>

            {/* Step 2: Backup Codes */}
            <div className="mb-6">
              <h4 className="font-medium mb-2">Step 2: Save Backup Codes</h4>
              <p className="text-sm text-gray-600 mb-3">
                Save these backup codes in a safe place. You can use them to access your account if you lose your phone.
              </p>
              <div className="bg-white border rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {setupData.backupCodes.map((code, index) => (
                    <code key={index} className="bg-gray-100 px-2 py-1 rounded text-sm">
                      {code}
                    </code>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => copyToClipboard(setupData.backupCodes.join('\n'))}
                    className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                  >
                    <FaCopy /> Copy All
                  </button>
                  <button
                    onClick={downloadBackupCodes}
                    className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                  >
                    <FaDownload /> Download
                  </button>
                </div>
              </div>
            </div>

            {/* Step 3: Verification */}
            <div>
              <h4 className="font-medium mb-2">Step 3: Verify Setup</h4>
              <p className="text-sm text-gray-600 mb-3">
                Enter the 6-digit code from your authenticator app to complete setup.
              </p>
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  value={verificationToken}
                  onChange={(e) => setVerificationToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="border rounded-lg px-3 py-2 text-center text-lg tracking-widest w-32"
                  maxLength="6"
                />
                <button
                  onClick={handleVerify2FA}
                  disabled={loading || verificationToken.length !== 6}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? "Verifying..." : "Verify & Enable"}
                </button>
                <button
                  onClick={() => {
                    setShowSetup(false);
                    setSetupData(null);
                    setVerificationToken("");
                    setError("");
                  }}
                  className="text-gray-600 hover:text-gray-800 px-4 py-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disable 2FA Section */}
      {twoFactorStatus.twoFactorEnabled && (
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="font-semibold text-lg mb-2">Disable Two-Factor Authentication</h3>
            <p className="text-gray-700 mb-4">
              Disabling 2FA will make your account less secure. Make sure you have other security measures in place.
            </p>
            
            {!showDisableForm ? (
              <button
                onClick={() => setShowDisableForm(true)}
                className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700"
              >
                Disable 2FA
              </button>
            ) : (
              <div className="space-y-3">
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    placeholder="Enter your password to confirm"
                    className="border rounded-lg px-3 py-2 pr-10 w-full max-w-md"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleDisable2FA}
                    disabled={loading || !disablePassword}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {loading ? "Disabling..." : "Confirm Disable"}
                  </button>
                  <button
                    onClick={() => {
                      setShowDisableForm(false);
                      setDisablePassword("");
                      setError("");
                    }}
                    className="text-gray-600 hover:text-gray-800 px-4 py-2"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSettings;